// How much memory `dz-runner.service` may hold, and whether a box's unit actually says
// so — epic #4739 slice 1. The monorepo gate children the coding agent spawns (`bun
// test`, up to 5.25 GB) share the daemon's cgroup; a unit with `MemoryMax=infinity`
// (or a fixed 4G), no `MemoryHigh` and `OOMPolicy=stop` thrashes the box for hours and
// then kills the daemon along with the child.
//
// ONE sizing rule, three emitters: the api's join script and `scripts/attach-vm.sh`
// render it as bash (attach-vm.sh is fetched standalone, so it carries a literal copy),
// and the domain reads it here. `scripts/memory-posture-parity.test.ts` holds the three
// equal. The constants below are what the join emitter interpolates, so a change here
// moves the api's copy with it; the attach-vm copy is caught by that parity test.

import { z } from 'zod';
import { BOX_ENROLL_RAM_FLOOR_BYTES } from './specs';

const MIB = 1024 * 1024;

/** MemoryMax is a whole multiple of this, rounded DOWN (never up past the box). */
export const UNIT_MEMORY_GRANULE_MIB = 256;
/** The reserve left to the kernel, page cache and sshd is at least this much... */
export const UNIT_MEMORY_RESERVE_FLOOR_MIB = 1536;
/** ...or one-in-this-many of MemTotal, whichever is larger. */
export const UNIT_MEMORY_RESERVE_DIVISOR = 8;
/** MemoryHigh = MemoryMax x NUMERATOR / DENOMINATOR: reclaim pressure before the OOM wall. */
export const UNIT_MEMORY_HIGH_NUMERATOR = 5;
/** See {@link UNIT_MEMORY_HIGH_NUMERATOR}. */
export const UNIT_MEMORY_HIGH_DENOMINATOR = 6;
/** The daemon's own headroom under MemoryHigh; the rest is the task cap (slice 2). */
export const UNIT_MEMORY_TASK_HEADROOM_MIB = 768;

/** A unit's memory bounds, in whole MiB — what systemd is told as `<n>M`. */
export type UnitMemorySizing = {
  memoryMaxMib: number;
  memoryHighMib: number;
  /** The coding task's own sub-cgroup cap at install time; the runner derives the live one from the unit's `memory.high` (`task-cgroup.ts`). */
  taskCapMib: number;
};

/**
 * Size `dz-runner.service` from the box's `MemTotal` in bytes. All arithmetic is
 * integer MiB with FLOOR division at every step — exactly what bash `$(( ))` does, so
 * the two emitters cannot disagree by a rounding mode:
 *
 *   mib     = floor(bytes / 2^20)
 *   reserve = max(1536, floor(mib / 8))
 *   max     = floor((mib - reserve) / 256) * 256
 *   high    = floor(max * 5 / 6)
 *   taskCap = high - 768
 *
 * Only MemoryMax takes the 256 MiB granule. MemoryHigh is its 5/6 floored to a whole
 * MiB: granuling it too would size the smallest admissible box (7600 MiB) at 4864M
 * rather than 4906M, and the owner's live 7.7 GiB 6G/5G fix is the table's first row.
 * HIGH < MAX still holds for every MAX > 0, and the pinned rows are in the test.
 *
 * THROWS under the enrollment RAM floor (7600 MiB, {@link BOX_ENROLL_RAM_FLOOR_BYTES}):
 * the join script refuses such a box before it sizes anything, so a smaller value here
 * is a caller bug, and a sizing that clamps it would hand a real box a unit it cannot
 * run under. Non-integer / non-finite input throws too.
 */
export function sizeUnitMemory(memTotalBytes: number): UnitMemorySizing {
  if (!Number.isSafeInteger(memTotalBytes) || memTotalBytes <= 0) {
    throw new RangeError(
      `sizeUnitMemory: MemTotal must be a positive integer byte count, got ${memTotalBytes}`,
    );
  }
  const mib = Math.floor(memTotalBytes / MIB);
  const floorMib = BOX_ENROLL_RAM_FLOOR_BYTES / MIB;
  if (memTotalBytes < BOX_ENROLL_RAM_FLOOR_BYTES) {
    throw new RangeError(
      `sizeUnitMemory: MemTotal ${mib} MiB is under the enrollment floor of ${floorMib} MiB — ` +
        'the box cannot hold a sized dz-runner.service; upgrade it and re-enroll',
    );
  }
  const granule = (v: number) => Math.floor(v / UNIT_MEMORY_GRANULE_MIB) * UNIT_MEMORY_GRANULE_MIB;
  const reserve = Math.max(
    UNIT_MEMORY_RESERVE_FLOOR_MIB,
    Math.floor(mib / UNIT_MEMORY_RESERVE_DIVISOR),
  );
  const memoryMaxMib = granule(mib - reserve);
  const memoryHighMib = Math.floor(
    (memoryMaxMib * UNIT_MEMORY_HIGH_NUMERATOR) / UNIT_MEMORY_HIGH_DENOMINATOR,
  );
  return {
    memoryMaxMib,
    memoryHighMib,
    taskCapMib: memoryHighMib - UNIT_MEMORY_TASK_HEADROOM_MIB,
  };
}

/**
 * Why a box runs its coding task UNCAPPED (epic #4739 slice 2) — the boot probe of
 * `apps/runner/src/task-cgroup.ts` could not stand the per-task cgroup up. Each names
 * the step that refused; that module's `bootTaskCgroups` docblock maps step to reason.
 */
export const TASK_CAP_UNAVAILABLE_REASONS = [
  'no_cgroup_v2',
  'container',
  'no_unit',
  'no_memory_controller',
  'not_delegated',
  'not_bounded',
  'unit_too_small',
  'daemon_move_failed',
  'controller_enable_failed',
] as const;
export type TaskCapUnavailableReason = (typeof TASK_CAP_UNAVAILABLE_REASONS)[number];

/**
 * The per-task memory cap this box ENFORCES (slice 2): `capped` with the bytes each
 * run's cgroup is held to, or `unavailable` with the reason it is not. Never omitted by
 * a binary that has the field, so "uncapped" can never read as "old binary".
 */
export const TaskCapPostureSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('capped'), bytes: z.number().int().positive() }),
  z.object({ kind: z.literal('unavailable'), reason: z.enum(TASK_CAP_UNAVAILABLE_REASONS) }),
]);
export type TaskCapPosture = z.infer<typeof TaskCapPostureSchema>;

/**
 * What the runner READ from its own unit, stamped on the presence beat
 * (`BoxStats.unitMemory`). A reading, never a claim: `null` bytes mean the cgroup file
 * said `max` (unbounded); an unreadable cgroup omits the whole posture instead.
 * `oomPolicy` is `null` when `systemctl show` could not answer — never defaulted to
 * `stop`, because "no data" must not read as the defect.
 */
export const UnitMemoryPostureSchema = z.object({
  /** `container` when the cgroup is `/` or `/.dockerenv` exists; `unknown` when no `*.service` segment. */
  supervisor: z.enum(['systemd', 'container', 'unknown']),
  /** The unit's cgroup path from `/proc/self/cgroup`, e.g. `/system.slice/dz-runner.service`. */
  cgroup: z.string().max(256),
  memoryMaxBytes: z.number().int().positive().nullable(),
  memoryHighBytes: z.number().int().positive().nullable(),
  oomPolicy: z.enum(['stop', 'continue', 'kill']).nullable(),
  /**
   * The runner may manage its unit's cgroup (`Delegate=yes`): proved by `cgroup.procs`
   * ownership when unprivileged, by systemd's own `Delegate` when root (root owns every
   * cgroup, so ownership proves nothing). Unanswerable reads `false`, never `true`.
   */
  delegated: z.boolean(),
  /**
   * The per-task cap (slice 2). A binary that HAS the field always sends it — an
   * uncapped box says `unavailable{reason}` rather than going quiet.
   *
   * `.catch(undefined)`, because this rides nested under `BoxStats` on the presence
   * beat: a runner that ships a NEW reason before the mothership deploys would
   * otherwise fail the WHOLE beat decode, and the box would read as not beating at
   * all. An unreadable cap drops the FIELD, never the beat — and a dropped field is
   * already "no data", which `unitMemoryPostureDefects` grades as no verdict.
   */
  taskCap: TaskCapPostureSchema.optional().catch(undefined),
  /**
   * Whether the unit carries the process-death reap (`ExecStopPost=… --reap-run-scopes`,
   * layer 2 of the runner's run-scope close), read from `systemctl show -p ExecStopPost`.
   * `null` when systemd could not answer (or the unit is a user unit). ABSENT from a
   * binary older than the field — and every binary that sends it knows the flag, so an
   * absent field means "update first", never "missing". Graded by `./reap-hook.ts`.
   */
  reapHook: z.boolean().nullable().optional().catch(undefined),
});
export type UnitMemoryPosture = z.infer<typeof UnitMemoryPostureSchema>;

export type UnitMemoryPostureDefect =
  | { kind: 'unbounded_max' }
  | { kind: 'unbounded_high' }
  | { kind: 'high_not_below_max' }
  | { kind: 'oom_policy_stop' }
  | { kind: 'not_delegated' };

/**
 * What is wrong with a box's unit, as a list (empty = healthy). Graded by supervisor,
 * because each can only be held to what it can express:
 *
 *   systemd    every defect
 *   container  `unbounded_max` only — docker's `--memory` is the one bound it sets;
 *              it has no MemoryHigh flag, no OOMPolicy and keeps its cgroup read-only
 *   unknown    nothing — no `*.service` segment means we do not know what we read
 *
 * An ABSENT posture (a binary older than the field, or an unreadable cgroup) yields no
 * defect: an unobservable signal never manufactures a verdict.
 */
export function unitMemoryPostureDefects(
  posture: UnitMemoryPosture | undefined,
): UnitMemoryPostureDefect[] {
  if (posture === undefined || posture.supervisor === 'unknown') return [];
  const defects: UnitMemoryPostureDefect[] = [];
  const { memoryMaxBytes: max, memoryHighBytes: high } = posture;
  if (max === null) defects.push({ kind: 'unbounded_max' });
  if (posture.supervisor === 'container') return defects;
  if (high === null) defects.push({ kind: 'unbounded_high' });
  if (max !== null && high !== null && high >= max) defects.push({ kind: 'high_not_below_max' });
  if (posture.oomPolicy === 'stop') defects.push({ kind: 'oom_policy_stop' });
  if (!posture.delegated) defects.push({ kind: 'not_delegated' });
  return defects;
}
